import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, functions, httpsCallable } from './firebase.js';
import { isDemoBuild } from './siteConfig.js';
import {
  AdminMonitoringSection,
  ADMIN_SECTIONS,
  useAdminMonitoring,
} from './AdminMonitoringPanels.js';

const SEARCH_LOCAL_NEWS_URL =
  'https://us-central1-nantahala-mountain-resor-596d9.cloudfunctions.net/searchLocalNews';

function computeHealthScore(signals = []) {
  return Math.max(
    0,
    Math.min(
      100,
      100
        - signals.filter((s) => s.severity === 'warning').length * 12
        - signals.filter((s) => s.severity === 'critical').length * 25,
    ),
  );
}

function AdminPortalPage({ user, isAdmin, isMobile }) {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('overview');
  const [actionMessage, setActionMessage] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [parcelSyncLoading, setParcelSyncLoading] = useState(false);

  const monitoring = useAdminMonitoring(activeSection);
  const { opsSnapshot, refreshSection, loading } = monitoring;

  const healthScore = useMemo(
    () => computeHealthScore(opsSnapshot?.signals),
    [opsSnapshot?.signals],
  );

  useEffect(() => {
    document.title = 'Admin Portal | Nantahala Mountain Resort';
    return () => {
      document.title = 'NMR Community';
    };
  }, []);

  if (!isAdmin) {
    return (
      <div className={`admin-portal-page ${isMobile ? 'admin-portal-page-mobile' : ''}`}>
        <div className="docs-card">
          <h1>Access denied</h1>
          <p>This area is restricted to community administrators.</p>
          <button type="button" className="admin-portal-btn" onClick={() => navigate('/dashboard')}>
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  const handleSyncAllParcels = async () => {
    if (isDemoBuild) {
      setActionMessage('Parcel GIS sync is disabled in the demo build.');
      return;
    }
    setParcelSyncLoading(true);
    setActionMessage('');
    try {
      const syncAllOwnerParcels = httpsCallable(functions, 'syncAllOwnerParcels');
      const result = await syncAllOwnerParcels();
      const synced = result.data?.synced || 0;
      const total = result.data?.total || 0;
      const failed = (result.data?.results || []).filter((row) => !row.synced);
      const failureHint = failed.length
        ? ` ${failed.length} unmatched — verify street address and owner name on Owner Contacts.`
        : '';
      setActionMessage(`Linked ${synced} of ${total} lots from Macon County assessor GIS.${failureHint}`);
    } catch (error) {
      setActionMessage(`Parcel sync failed: ${error.message}`);
    } finally {
      setParcelSyncLoading(false);
    }
  };

  const handleNewsRefresh = async () => {
    setActionLoading(true);
    setActionMessage('');
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        throw new Error('Not authenticated');
      }
      const response = await fetch(SEARCH_LOCAL_NEWS_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      });
      if (!response.ok) {
        throw new Error(`News refresh failed (${response.status})`);
      }
      setActionMessage('News feed updated. Reload the dashboard to see changes.');
    } catch (error) {
      setActionMessage(`Failed to refresh news: ${error.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const generatedAt = opsSnapshot?.generatedAt
    ? new Date(opsSnapshot.generatedAt).toLocaleString()
    : null;

  return (
    <div className={`admin-portal-page ${isMobile ? 'admin-portal-page-mobile' : ''}`}>
      <div className="admin-portal-view admin-portal-view--wide">
        <section className="admin-hero">
          <div className="admin-hero-glow" aria-hidden="true" />
          <div className="admin-hero-content">
            <div>
              <p className="admin-eyebrow">Platform command center</p>
              <h2 className="admin-title">NMR Admin Portal</h2>
              <p className="admin-subtitle">
                Signed in as <strong>{user?.email}</strong>
                {generatedAt ? <> · Updated {generatedAt}</> : null}
              </p>
            </div>
            <div className="admin-hero-actions">
              <div
                className="admin-health-ring"
                style={{ '--health': healthScore }}
                aria-label={`Platform health score ${healthScore}`}
              >
                <span className="admin-health-value">{healthScore}</span>
                <span className="admin-health-label">Health</span>
              </div>
              <button
                type="button"
                className="admin-portal-btn admin-portal-btn-primary"
                disabled={loading || actionLoading}
                onClick={() => refreshSection()}
              >
                {loading ? 'Refreshing…' : 'Refresh metrics'}
              </button>
              <button
                type="button"
                className="admin-portal-btn"
                disabled={actionLoading}
                onClick={handleNewsRefresh}
              >
                {actionLoading ? 'Refreshing…' : 'Refresh news feed'}
              </button>
              <button
                type="button"
                className="admin-portal-btn"
                disabled={parcelSyncLoading || actionLoading}
                onClick={handleSyncAllParcels}
              >
                {parcelSyncLoading ? 'Syncing parcels…' : 'Sync all parcels from assessor'}
              </button>
            </div>
          </div>
        </section>

        <nav className="admin-section-nav" aria-label="Admin sections">
          {ADMIN_SECTIONS.map((section) => (
            <button
              key={section.id}
              type="button"
              className={`admin-section-nav-btn${activeSection === section.id ? ' admin-section-nav-btn--active' : ''}`}
              onClick={() => setActiveSection(section.id)}
            >
              {section.label}
            </button>
          ))}
        </nav>

        {actionMessage && (
          <div className="admin-portal-alert" role="status">
            {actionMessage}
          </div>
        )}

        <AdminMonitoringSection section={activeSection} monitoring={monitoring} />

        {activeSection !== 'tools' && (
          <section className="admin-portal-panel admin-portal-panel-compact">
            <header className="admin-portal-panel-header">
              <h3>Quick links</h3>
            </header>
            <div className="admin-portal-quick-links">
              <button type="button" className="admin-portal-btn" onClick={() => navigate('/dashboard')}>
                Owner dashboard
              </button>
              <button type="button" className="admin-portal-btn" onClick={() => navigate('/help')}>
                Owner help guide
              </button>
              <button type="button" className="admin-portal-btn" onClick={() => navigate('/preferences')}>
                Your preferences
              </button>
              <button type="button" className="admin-portal-btn" onClick={() => navigate('/violations')}>
                Violations
              </button>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

export default AdminPortalPage;